import { Checkbox, Chip, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import { observer } from "mobx-react";
import React, { useState, useCallback, useMemo } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import { Card } from "randmarcomps";
import withManufacturer from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Invoice } from "../../stores/document/Document";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";

interface IProps {
  invoices?: Invoice[];
  defaultRowsPerPage?: number;
  applicationId: string;
}

const InvoicesGrid: React.FC<IProps> = ({ invoices, defaultRowsPerPage = 20, applicationId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [showOnlyUnpaid, setShowOnlyUnpaid] = useState(false);

  const handleChangePage = useCallback((_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleShowOnlyUnpaidChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyUnpaid(event.target.checked);
  }, []);

  const filteredInvoices = useMemo(() => {
    if (!invoices) return [];
    return invoices
      .slice()
      .sort((a, b) => (b.invoiceDate || 0) - (a.invoiceDate || 0) || (b.invoiceNumber || "").localeCompare(a.invoiceNumber || ""))
      .filter(v => (showOnlyUnpaid ? v.payed === false : true));
  }, [invoices, showOnlyUnpaid]);

  if (!invoices || invoices.length === 0) {
    return (
      <div className="grid place-items-center text-center p-4 border rounded-lg bg-white">
        <h3 className="font-semibold text-lg text-gray-800">What are Invoices?</h3>
        <p className="text-gray-600">Invoices are bills for Purchase Orders sent by Randmar to your company.</p>
        <p className="text-gray-500 mt-2">There are no invoices to display.</p>
      </div>
    );
  }

  return (
    <Card className="w-full">
      <div className="w-full overflow-auto rounded-[20px]">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>
                <div className="flex justify-center items-center space-x-1">
                  <Checkbox checked={showOnlyUnpaid} onChange={handleShowOnlyUnpaidChange} size="small" />
                  <label className="text-sm cursor-pointer select-none" onClick={() => setShowOnlyUnpaid(!showOnlyUnpaid)}>
                    Show only unpaid
                  </label>
                </div>
              </TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Document Type</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>PO Number</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Invoice Number</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Invoice Date</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Sub Total</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Tax 1</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Tax 2</TableCell>
              <TableCell align="center" sx={{ borderColor: "#582c83" }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"Invoice" + index}>
                  <TableCell>
                    <Chip variant={row.payed ? "outlined" : "filled"} label={row.payed ? "Paid" : "Unpaid"} color="primary" size="small" />
                  </TableCell>
                  <TableCell align="center">{row.documentType}</TableCell>
                  <TableCell align="center">
                    <Link className="text-blue-600 hover:underline" to={Path.document(applicationId, row.purchaseOrderNumber)}>
                      {row.purchaseOrderNumber}
                    </Link>
                  </TableCell>
                  <TableCell align="center">{row.invoiceNumber}</TableCell>
                  <TableCell align="center">{FormatDateFromInt(row.invoiceDate)}</TableCell>
                  <TableCell align="center">
                    <FormattedNumber {...CurrencyUtils.format} value={row.subTotal || 0} />
                  </TableCell>
                  <TableCell align="center">{`$${row.taxAmount1} (${row.taxRate1}%)`}</TableCell>
                  <TableCell align="center">{`$${row.taxAmount2} (${row.taxRate2}%)`}</TableCell>
                  <TableCell align="center">
                    <FormattedNumber {...CurrencyUtils.format} value={row.total || 0} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          {filteredInvoices.length > rowsPerPage && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={filteredInvoices.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ "& .MuiTablePagination-spacer": { display: "none" }, "& .MuiTablePagination-toolbar": { justifyContent: "flex-start" } }}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </Card>
  );
};

export default withManufacturer(observer(InvoicesGrid));
